import { Injectable, computed, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GlobalLoadingService {
  private readonly minimumVisibleMs = 350;
  private readonly blockingCount = signal(0);
  private readonly contentLoading = signal(false);
  private readonly contentHoldLoading = signal(false);
  private contentVisibleStartedAt = 0;
  private contentHideTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isBlockingLoading = computed(() => this.blockingCount() > 0);
  readonly isContentLoading = computed(
    () => this.contentLoading() || this.contentHoldLoading(),
  );

  show(): void {
    this.blockingCount.update((count) => count + 1);
  }

  hide(): void {
    this.blockingCount.update((count) => Math.max(0, count - 1));
  }

  startRouteLoading(): void {
    this.markContentVisibleStarted();
    this.contentLoading.set(true);
  }

  stopRouteLoading(): void {
    this.contentLoading.set(false);
    this.holdContentIfIdle();
  }

  private markContentVisibleStarted(): void {
    if (!this.isContentLoading()) {
      this.contentVisibleStartedAt = Date.now();
    }

    this.contentHoldLoading.set(false);
    this.clearContentHideTimer();
  }

  private holdContentIfIdle(): void {
    if (this.contentLoading()) {
      return;
    }

    const elapsedMs = Date.now() - this.contentVisibleStartedAt;
    const remainingMs = Math.max(0, this.minimumVisibleMs - elapsedMs);

    if (remainingMs <= 0) {
      this.contentHoldLoading.set(false);
      return;
    }

    this.contentHoldLoading.set(true);
    this.clearContentHideTimer();
    this.contentHideTimer = setTimeout(() => {
      this.contentHoldLoading.set(false);
      this.contentHideTimer = null;
    }, remainingMs);
  }

  private clearContentHideTimer(): void {
    if (!this.contentHideTimer) {
      return;
    }

    clearTimeout(this.contentHideTimer);
    this.contentHideTimer = null;
  }
}
